// hooks
import usePreviousValue from '@hooks/use-previous-value';
// utils
import {
  generateSocialLinkIconImage,
  socialLinksAppearanceHaveChanged,
} from '@modules/email-signatures-helpers';
import { isEqual } from 'es-toolkit';
// es-toolkit/compat: lodash filter iterates objects/Records (not in es-toolkit main)
import { filter } from 'es-toolkit/compat';
import { useEffect, useMemo } from 'react';

// consts and types
import type {
  EmailSignatureSocialLink,
  EmailSignatureSocialLinkId,
  EmailSignatureV2,
} from '../types';

/**
 * This hook will detect any changes to the email signature social links and generate new
 * social links icons accordingly.
 * @param emailSignature The email signature object
 * @param handleUpdateNewLocalSocialLinkIconUrl The callback function that is used to update the
 * social links icon with the newly generated icons
 * @param enable The flag indicates whether the hook should run
 */
function useSocialLinksIconsGenerator(
  emailSignature: EmailSignatureV2,
  handleUpdateNewLocalSocialLinkIconUrl: (
    socialLinkId: EmailSignatureSocialLinkId,
    imageUrl: string,
  ) => void,
  enable: boolean,
): void {
  const previousEmailSignature = usePreviousValue(
    emailSignature,
    (prevEmailSignature, currentEmailSignature) =>
      enable ? isEqual(prevEmailSignature, currentEmailSignature) : true,
  );

  const socialLinksThatHaveNewIcons = useMemo(() => {
    if (!enable || !previousEmailSignature) return [];
    const previousEmailSignatureSocialLinks = previousEmailSignature.socialLinks;

    // we filter out the social links that have new icons that needed to be regenerated,
    // a social link will have a new icon when its appearance change or it is a new social link
    return filter(
      emailSignature.socialLinks,
      (socialLink) =>
        !isEqual(socialLink.id, previousEmailSignatureSocialLinks[socialLink.id]?.id) ||
        socialLinksAppearanceHaveChanged(previousEmailSignature.design, emailSignature.design),
    );
  }, [emailSignature.design, emailSignature.socialLinks, enable, previousEmailSignature]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    // For each social link that has a new icon, we will generate a new icon, convert
    // it to image and call a callback update function with this new icon image
    socialLinksThatHaveNewIcons.forEach((socialLinkThatHasNewIcon) => {
      const iconImageDataUrl = generateSocialLinkIconImage(
        socialLinkThatHasNewIcon.type,
        emailSignature.design,
      );

      handleUpdateNewLocalSocialLinkIconUrl(socialLinkThatHasNewIcon.id, iconImageDataUrl ?? '');
    });
  }, [
    emailSignature.design,
    emailSignature.socialLinks,
    handleUpdateNewLocalSocialLinkIconUrl,
    previousEmailSignature,
    enable,
    socialLinksThatHaveNewIcons,
  ]);
}

/**
 * This hook is similar to the hook above, but it only generates new icons for the dummy social links
 * @param emailSignature The email signature object
 * @param handleUpdateDummySocialLinkIconUrl The callback function that is used to update the dummy
 * social links icon with the newly generated icon
 * @param standardCompactSocialLinks The dummy social links
 */
export function useDummySocialLinksIconsGenerator(
  emailSignature: EmailSignatureV2,
  handleUpdateDummySocialLinkIconUrl: (index: number, imageUrl: string) => void,
  standardCompactSocialLinks: EmailSignatureSocialLink[],
): void {
  useEffect(() => {
    standardCompactSocialLinks.forEach((dummySocialLink, index) => {
      const iconImageDataUrl = generateSocialLinkIconImage(
        dummySocialLink.type,
        emailSignature.design,
      );

      handleUpdateDummySocialLinkIconUrl(index, iconImageDataUrl ?? '');
    });
  }, [standardCompactSocialLinks, emailSignature.design, handleUpdateDummySocialLinkIconUrl]);
}

export default useSocialLinksIconsGenerator;
